import { getScoreColor } from "@/components/readonly/scoring";
import { Skeleton } from "@/components/shared/Skeleton";
import { TreeProjectRow } from "@/components/teacher/score/TreeProjectRow";
import type { ReportLessonTreeQuery } from "@/generated/graphql";
import { format, useT } from "@/i18n";
import { HiChevronLeft, HiOutlineFolder } from "react-icons/hi";

type TreeLevel = ReportLessonTreeQuery["reportLessonTree"];
type TreeProject = TreeLevel["projects"][number];
type LessonTiles = TreeLevel["lessons"][number];

interface LessonTreeCardProps {
  level: TreeLevel | undefined;
  /** The folders walked into, outermost first — drives the crumb trail. */
  path: readonly { projectId: string; name: string }[];
  onEnterProject: (project: { projectId: string; name: string }) => void;
  onBack: () => void;
  loading?: boolean;
  /** A cold failure for this level — distinct from a level that is genuinely empty. */
  failed?: boolean;
}

/** An unplayed slide — no score to colour it by. */
const UNDONE_TILE_COLOR = "#DFDFDF";
/** A lesson with nothing scored yet. Darker than an unplayed tile so the dash stays readable, and
 * deliberately not a colour — "not started" must not read as a fourth score band. */
const NO_SCORE_COLOR = "#A3A3A3";
const NO_SCORE_LABEL = "—";
const CRUMB_JOIN = " › ";

/** Keys built once at module scope, so the grid below maps over ids rather than indexes. */
const skeletonTiles = (prefix: string, count: number) =>
  Array.from({ length: count }, (_, index) => `${prefix}-${index}`);

// Two lessons' worth, at lengths a real lesson has: the loading state should be the shape the
// content arrives in, not a stack of generic rows.
const SKELETON_LESSONS = [
  { id: "first", nameWidth: "w-24", tiles: skeletonTiles("first", 46) },
  { id: "second", nameWidth: "w-16", tiles: skeletonTiles("second", 32) },
] as const;

/** One tile per slide, on the same 5px square the calendar's day cells use. */
function Tile({ score }: { score: number | null }) {
  const done = score != null;

  return (
    <span
      className="flex h-[34px] w-[34px] items-center justify-center rounded-[5px] text-[11px] font-bold text-white"
      style={{ backgroundColor: done ? getScoreColor(score) : UNDONE_TILE_COLOR }}
    >
      {done ? Math.round(score) : ""}
    </span>
  );
}

/** The shape a lesson arrives in: name, pill, the done count, then its grid of slides. */
function LessonSkeleton({ lesson }: { lesson: (typeof SKELETON_LESSONS)[number] }) {
  return (
    <div className="border-gray-100 border-b py-3">
      <div className="flex items-center gap-2">
        <Skeleton className={`h-4 ${lesson.nameWidth}`} />
        <span className="flex-1" />
        <Skeleton className="h-[26px] w-[44px] rounded-full" />
      </div>
      <Skeleton className="mt-1.5 mb-2 h-3 w-28" />
      <div className="flex flex-wrap gap-[5px]">
        {lesson.tiles.map((tileKey) => (
          <Skeleton key={tileKey} className="h-[34px] w-[34px] rounded-[5px]" />
        ))}
      </div>
    </div>
  );
}

/**
 * The lesson's own average, in the same colour language the tiles use. It sits beside the name
 * rather than behind the block: the grid already carries colour, and a wash under it would
 * compete with the very scores it is summarising.
 */
function LessonScorePill({ average }: { average: number | null }) {
  const t = useT();
  const color = average == null ? NO_SCORE_COLOR : getScoreColor(average);

  return (
    <span
      className="flex h-[26px] min-w-[44px] shrink-0 items-center justify-center rounded-full px-2 font-kumbh font-bold text-[14px]"
      style={{ backgroundColor: `${color}1F`, color }}
      aria-label={
        average == null ? undefined : format(t["report.lessons.averageScore"], { score: average })
      }
    >
      {average ?? NO_SCORE_LABEL}
    </span>
  );
}

/** A lesson is read, not entered: its slides sit right under its name. */
function LessonBlock({ lesson }: { lesson: LessonTiles }) {
  const t = useT();
  const scored = lesson.slides.filter((slide) => slide.score != null);
  const average =
    scored.length === 0
      ? null
      : Math.round(scored.reduce((sum, slide) => sum + (slide.score ?? 0), 0) / scored.length);

  return (
    <div className="border-gray-100 border-b py-3">
      <div className="flex items-center gap-2">
        <p className="flex-1 truncate font-kumbh font-bold text-[14px] text-app-ink">
          {lesson.name}
        </p>
        <LessonScorePill average={average} />
      </div>
      {/* Kept next to the pill: an average over 3 of 18 slides looks confident on its own. */}
      <p className="body-sm mb-2 text-[11px] text-app-muted">
        {format(t["report.lessons.slidesDone"], {
          done: scored.length,
          total: lesson.slides.length,
        })}
      </p>
      <div className="flex flex-wrap gap-[5px]">
        {lesson.slides.map((slide) => (
          <Tile key={slide.slideId} score={slide.score ?? null} />
        ))}
      </div>
    </div>
  );
}

/** The lessons of a folder the server sent in full, under its own name. */
function InlineProjectBlock({ project }: { project: TreeProject }) {
  const t = useT();
  const countLabel = format(
    t[
      project.lessons.length === 1
        ? "report.lessons.lessonCountOne"
        : "report.lessons.lessonCountOther"
    ],
    { count: project.lessons.length }
  );

  return (
    <div>
      <div className="flex items-center gap-2 pt-3 pb-1">
        <HiOutlineFolder className="h-4 w-4 shrink-0 text-app-muted" />
        <p className="flex-1 truncate font-kumbh font-bold text-[14px] text-app-ink">
          {project.name}
        </p>
        <span className="text-[11px] text-app-muted">{countLabel}</span>
      </div>
      <div className="pl-3">
        {project.lessons.map((lesson) => (
          <LessonBlock key={lesson.lessonId} lesson={lesson} />
        ))}
      </div>
    </div>
  );
}

/**
 * The student's lessons as their teacher sees them: a folder holding more folders is a row to
 * open, a folder holding only lessons is rendered where it sits, and every lesson shows all of
 * its slides — grey until played, then coloured by score. The server decides which is which
 * (`holdsProjects`), so this mirrors the student app's own Lessons tab.
 */
export function LessonTreeCard({
  level,
  path,
  onEnterProject,
  onBack,
  loading,
  failed,
}: LessonTreeCardProps) {
  const t = useT();
  // Openable rows first: they'd otherwise be easy to miss between two tall tile grids.
  const navigable = level?.projects.filter((project) => project.holdsProjects) ?? [];
  const inlineProjects = level?.projects.filter((project) => !project.holdsProjects) ?? [];
  const lessons = level?.lessons ?? [];
  const isEmpty = navigable.length === 0 && inlineProjects.length === 0 && lessons.length === 0;

  return (
    <div className="app-card px-5 py-4">
      <p className="text-[11px] font-extrabold uppercase tracking-wider text-app-muted">
        {t["report.lessons.title"]}
      </p>

      {path.length > 0 && (
        <button
          type="button"
          onClick={onBack}
          aria-label={t["report.lessons.back"]}
          className="mt-2 flex w-full items-center gap-1 border-gray-100 border-b pb-2 text-left"
        >
          <HiChevronLeft className="h-4 w-4 shrink-0 text-app-ink" />
          <span className="flex-1 truncate text-[12px] font-bold text-app-ink">
            {path.map((node) => node.name).join(CRUMB_JOIN)}
          </span>
        </button>
      )}

      {loading ? (
        <div className="mt-1">
          {SKELETON_LESSONS.map((lesson) => (
            <LessonSkeleton key={lesson.id} lesson={lesson} />
          ))}
        </div>
      ) : failed ? (
        <p className="body-sm mt-3 text-app-overdue">{t["student.reportLoadError"]}</p>
      ) : isEmpty ? (
        <p className="body-sm mt-3 text-app-muted">{t["report.lessons.empty"]}</p>
      ) : (
        <div className="mt-1">
          {navigable.map((project) => (
            <TreeProjectRow
              key={project.projectId}
              name={project.name}
              imageUrl={project.imageUrl}
              caption={format(
                t[
                  project.itemCount === 1
                    ? "report.lessons.itemCountOne"
                    : "report.lessons.itemCountOther"
                ],
                { count: project.itemCount }
              )}
              onPress={() => onEnterProject(project)}
            />
          ))}
          {lessons.map((lesson) => (
            <LessonBlock key={lesson.lessonId} lesson={lesson} />
          ))}
          {inlineProjects.map((project) => (
            <InlineProjectBlock key={project.projectId} project={project} />
          ))}
        </div>
      )}
    </div>
  );
}
